'use client';

import { create } from 'zustand';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { tourApi } from '@/features/tour/services/tour-api';
import { getTour, type TourStep } from '@/features/tour/lib/tour-registry';

/**
 * Hook GLOBAL multi-tour — singleton via Zustand. Un singur tour activ la un moment dat.
 *
 * Faze:
 *  - 'idle'     → niciun tour activ
 *  - 'welcome'  → modal de bun-venit (pentru tour-ul activ)
 *  - 'running'  → tooltip + spotlight pe pasul curent
 *  - 'finished' → toast/celebration scurt; revine la 'idle' după close()
 */
export type TourPhase = 'idle' | 'welcome' | 'running' | 'finished';

type TourState = {
  /** ID-ul tour-ului activ. null când phase === 'idle'. */
  activeTourId: string | null;
  phase: TourPhase;
  stepIndex: number;
  openWelcome: (tourId: string) => void;
  start: (tourId: string) => void;
  next: () => void;
  prev: () => void;
  goTo: (i: number) => void;
  complete: () => Promise<void>;
  /**
   * Marchează un tour ca sărit. Dacă nu primește tourId, folosește
   * activeTourId din store (cazul tour-ului care rulează acum).
   * Dacă primește tourId, marchează acel tour ca skipped chiar dacă nu e
   * activ (cazul X de pe TourPromptBanner — tour-ul nu e activ, e doar propus).
   */
  skip: (tourId?: string) => Promise<void>;
  restart: (tourId: string) => Promise<void>;
  close: () => void;
};

const useTourStore = create<TourState>((set, get) => ({
  activeTourId: null,
  phase: 'idle',
  stepIndex: 0,

  openWelcome: (tourId) => {
    const tour = getTour(tourId);
    // Pentru tururi fără welcome modal, sare direct la running
    const initialPhase: TourPhase = tour?.hasWelcomeModal ? 'welcome' : 'running';
    set({ activeTourId: tourId, phase: initialPhase, stepIndex: 0 });
  },
  start: (tourId) => set({ activeTourId: tourId, phase: 'running', stepIndex: 0 }),
  next: () => {
    const { activeTourId, stepIndex } = get();
    if (!activeTourId) return;
    const tour = getTour(activeTourId);
    if (!tour) return;
    set({ stepIndex: Math.min(stepIndex + 1, tour.steps.length - 1) });
  },
  prev: () => set((s) => ({ stepIndex: Math.max(s.stepIndex - 1, 0) })),
  goTo: (i) => {
    const { activeTourId } = get();
    if (!activeTourId) return;
    const tour = getTour(activeTourId);
    if (!tour) return;
    set({ stepIndex: Math.max(0, Math.min(i, tour.steps.length - 1)) });
  },

  complete: async () => {
    const { activeTourId } = get();
    if (!activeTourId) return;
    set({ phase: 'finished' });
    try {
      await tourApi.markCompleted(activeTourId);
      await useAuthStore.getState().refreshProfile();
    } catch {
      /* non-blocking */
    }
  },

  skip: async (tourId) => {
    const { activeTourId } = get();
    const targetTourId = tourId ?? activeTourId;
    if (!targetTourId) return;
    // Închide tour-ul activ doar dacă e cel pe care îl sărim.
    if (activeTourId === targetTourId) {
      set({ phase: 'idle', activeTourId: null });
    }
    try {
      await tourApi.markSkipped(targetTourId);
      await useAuthStore.getState().refreshProfile();
    } catch {
      /* non-blocking */
    }
  },

  restart: async (tourId) => {
    try {
      await tourApi.restart(tourId);
      await useAuthStore.getState().refreshProfile();
    } catch {
      /* ignore */
    }
    const tour = getTour(tourId);
    const initialPhase: TourPhase = tour?.hasWelcomeModal ? 'welcome' : 'running';
    set({ activeTourId: tourId, phase: initialPhase, stepIndex: 0 });
  },

  close: () => set({ phase: 'idle', activeTourId: null }),
}));

/**
 * Hook generic — returnează state + acțiuni pentru un tour specific (după ID).
 * `isActive` e true doar dacă tour-ul curent activ în store coincide cu cel cerut.
 */
export function useTour(tourId: string) {
  const activeTourId = useTourStore((s) => s.activeTourId);
  const phase = useTourStore((s) => s.phase);
  const stepIndex = useTourStore((s) => s.stepIndex);

  const user = useAuthStore((s) => s.user);
  const isCompleted = !!user?.uiCompletedTours?.includes(tourId);
  const isSkipped = !!user?.uiSkippedTours?.includes(tourId) && !isCompleted;
  const shouldAutoStart = !!user && !isCompleted && !isSkipped;

  const tour = getTour(tourId);
  const isActive = activeTourId === tourId;
  const currentStep: TourStep | null =
    isActive && phase === 'running' && tour ? tour.steps[stepIndex] ?? null : null;
  const totalSteps = tour?.steps.length ?? 0;

  return {
    tourId,
    isActive,
    phase: isActive ? phase : 'idle',
    stepIndex: isActive ? stepIndex : 0,
    currentStep,
    totalSteps,
    isCompleted,
    isSkipped,
    shouldAutoStart,
    openWelcome: () => useTourStore.getState().openWelcome(tourId),
    start: () => useTourStore.getState().start(tourId),
    next: useTourStore.getState().next,
    prev: useTourStore.getState().prev,
    goTo: useTourStore.getState().goTo,
    complete: useTourStore.getState().complete,
    skip: () => useTourStore.getState().skip(tourId),
    restart: () => useTourStore.getState().restart(tourId),
    close: useTourStore.getState().close,
  };
}

/**
 * Backward-compat: vechiul useProductTour() echivalent cu useTour('overview').
 */
export function useProductTour() {
  return useTour('overview');
}

/**
 * Acces direct la state-ul global — utilizat de TourTriggerMount pentru a ști
 * care tour rulează (pentru a alege ce steps + ce welcome modal să randeze).
 */
export function useActiveTour() {
  const activeTourId = useTourStore((s) => s.activeTourId);
  const phase = useTourStore((s) => s.phase);
  const stepIndex = useTourStore((s) => s.stepIndex);
  return { activeTourId, phase, stepIndex };
}
